import React from 'react';
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Edit as EditIcon,
  Archive as ArchiveIcon,
  Visibility as VisibilityIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Emergency as EmergencyIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import KanbanBoard from '../../common/KanbanBoard';
import {
  REQUIREMENT_STATUS_COLUMNS,
  resolveRequirementStatus,
  getRequirementStatusMeta,
  compareRequirementKanbanItems,
} from '../../../utils/requirementStatus';
import { canEditRequirement, canCancelRequirement } from '../../../utils/partnershipPermissions';

const RequirementKanbanCard = ({ requirement, user, onOpen, onEdit, onCancel, draggable, isDragging }) => {
  const theme = useTheme();
  const statusMeta = getRequirementStatusMeta(resolveRequirementStatus(requirement));

  return (
    <Box
      component={motion.div}
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      sx={{
        p: 1.5,
        mb: 1.25,
        borderRadius: '12px',
        cursor: draggable ? 'grab' : 'pointer',
        background: 'linear-gradient(160deg, rgba(15,23,42,0.95) 0%, rgba(30,41,59,0.9) 100%)',
        border: `1px solid ${alpha(statusMeta.color, 0.25)}`,
        boxShadow: isDragging
          ? `0 12px 32px ${alpha(statusMeta.color, 0.35)}`
          : '0 4px 16px rgba(0,0,0,0.25)',
        opacity: isDragging ? 0.85 : 1,
        transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
        '&:hover': {
          borderColor: alpha(statusMeta.color, 0.5),
          boxShadow: `0 8px 24px ${alpha(statusMeta.color, 0.2)}`,
        },
        '&:active': draggable ? { cursor: 'grabbing' } : undefined,
      }}
      onClick={() => onOpen(requirement)}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: '0.9rem',
            color: '#fff',
            lineHeight: 1.3,
            fontFamily: "'DM Sans', sans-serif",
            flex: 1,
          }}
        >
          {requirement.wanted_item || '—'}
        </Typography>
        {requirement.is_emergency && (
          <Chip
            icon={<EmergencyIcon sx={{ fontSize: '14px !important' }} />}
            label="Urgent"
            size="small"
            sx={{
              height: 20,
              fontSize: '0.58rem',
              fontWeight: 700,
              bgcolor: alpha('#F87171', 0.15),
              color: '#F87171',
              border: '1px solid rgba(248,113,113,0.35)',
              flexShrink: 0,
            }}
          />
        )}
      </Box>

      {requirement.amount && (
        <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.55)', mb: 0.75 }}>
          Amount: {requirement.amount}
        </Typography>
      )}

      {requirement.purpose ? (
        <Typography
          sx={{
            fontSize: '0.75rem',
            color: 'rgba(255,255,255,0.55)',
            mb: 1,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {requirement.purpose}
        </Typography>
      ) : null}

      {requirement.company_name && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75 }}>
          <BusinessIcon sx={{ fontSize: 14, color: 'rgba(255,255,255,0.35)' }} />
          <Typography sx={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.5)' }} noWrap>
            {requirement.company_name}
          </Typography>
        </Box>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
          <PersonIcon sx={{ fontSize: 14, color: 'rgba(255,255,255,0.35)' }} />
          <Typography sx={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.5)' }} noWrap>
            {requirement.responsible?.name || 'Unassigned'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.25 }} onClick={(e) => e.stopPropagation()}>
          <Tooltip title="View" arrow>
            <IconButton size="small" onClick={() => onOpen(requirement)} sx={{ color: theme.palette.info.main }}>
              <VisibilityIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          {user && canEditRequirement(user, requirement) && onEdit && (
            <Tooltip title="Edit" arrow>
              <IconButton size="small" onClick={() => onEdit(requirement)} sx={{ color: '#f59e0b' }}>
                <EditIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
          {onCancel && canCancelRequirement(user, requirement) && (
            <Tooltip title="Archive requirement" arrow>
              <IconButton size="small" onClick={() => onCancel(requirement)} sx={{ color: theme.palette.error.main }}>
                <ArchiveIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const RequirementKanban = ({
  requirements,
  user,
  canDrag,
  onStatusChange,
  onOpenRequirement,
  onEditRequirement,
  onCancelRequirement,
  updatingRequirementId,
}) => (
  <KanbanBoard
    columns={REQUIREMENT_STATUS_COLUMNS}
    items={requirements}
    getItemId={(r) => r.id}
    resolveColumnId={resolveRequirementStatus}
    sortItems={compareRequirementKanbanItems}
    canDragItem={(r) => canDrag(r)}
    onColumnChange={onStatusChange}
    updatingItemId={updatingRequirementId}
    dragMimeType="application/x-requirement-id"
    emptyMessage="Drop requirements here"
    renderCard={(requirement, { isDragging, draggable }) => (
      <RequirementKanbanCard
        requirement={requirement}
        user={user}
        onOpen={onOpenRequirement}
        onEdit={onEditRequirement}
        onCancel={onCancelRequirement}
        draggable={draggable}
        isDragging={isDragging}
      />
    )}
  />
);

export default RequirementKanban;
